import { Component, Inject, Input, OnChanges, OnInit, Optional, SimpleChanges } from '@angular/core';
import { Grid } from '@app/core/components/grid/grid.model';
import { GridOptions } from '@app/core/components/grid/grid.types';
import { ModalService } from '@app/core/services/modal.service';
import { TT_VIEW_NAVIGATOR, TTViewNavigator, useNavigatorFor } from '@app/modal/state-modal/state-modal.component';
import { AcCustTransesGet, AcCustTransesGridRow, AcCustTransService, GetAcCustTransesParams } from '@app/services/ac-cust-trans.service';
import { BusinesscoItem, LogisticService } from '@app/services/logistic.service';
import { CustomerAccountInfo, TypeaheadService } from '@app/services/typeahead.service';

@Component({
    selector: 'tt-ac-cust-transes',
    templateUrl: './ac-cust-transes.component.html',
    styleUrls: ['./ac-cust-transes.component.css'],
    standalone: false,
})
export class AcCustTransesComponent implements OnInit, OnChanges {
    @Input()
    public params?: GetAcCustTransesParams;

    public page?: AcCustTransesGet;

    public companies: BusinesscoItem[] = [];

    public grid?: GridOptions<AcCustTransesGridRow>;

    public removing = false;

    public matching = false;

    public autoMatching = false;

    constructor(
        private acCustTrans: AcCustTransService,
        private logistic: LogisticService,
        private typeahead: TypeaheadService,
        private modal: ModalService,
        @Optional() @Inject(TT_VIEW_NAVIGATOR) private navigator?: TTViewNavigator,
    ) {
        this.navigator = useNavigatorFor(navigator, this);
    }

    public searchCustomers = ({ value }: { value: string | null }): Promise<CustomerAccountInfo[]> => {
        return this.typeahead.searchCustomerAccountInfo({ cust_name: value ?? '', custtype: 'C' });
    };

    public readGrid(): void {
        this.grid?.gridfunc?.read({ reloadColschema: false });
    }

    public readGridWithColschema(): void {
        this.grid?.gridfunc?.read({ reloadColschema: true });
    }

    public async removeMatching(): Promise<void> {
        if (!this.page) return;

        try {
            this.removing = true;

            const { errorcode, errormessage } = await this.acCustTrans.removeAcCustTransesMatching(this.page);

            if (errorcode !== '0') {
                this.modal.show({ type: 'warning', title: 'accusttranses_remove_matching_show_title', translateTitle: true, message: errormessage });
            } else {
                this.readGrid();
            }
        } catch (error) {
            console.error(error);
        } finally {
            this.removing = false;
        }
    }

    public async match(): Promise<void> {
        if (!this.page) return;

        try {
            this.matching = true;

            const records = (this.grid?.gridfunc?.getIsSelectedRows() ?? []).map(({ accusttrans_keyno }) => ({ accusttrans_keyno }));
            const { errorcode, errormessage } = await this.acCustTrans.matchSelectedAcCustTranses({ ...this.page, records });

            if (errorcode !== '0') {
                this.modal.show({ type: 'warning', title: 'accusttranses_match_selected_show_title', translateTitle: true, message: errormessage });
            } else {
                this.readGrid();
            }
        } catch (error) {
            console.error(error);
        } finally {
            this.matching = false;
        }
    }

    public async autoMatch(): Promise<void> {
        if (!this.page) return;

        try {
            this.autoMatching = true;

            const { errorcode, errormessage } = await this.acCustTrans.autoMatchAcCustTranses(this.page);

            if (errorcode !== '0') {
                this.modal.show({ type: 'warning', title: 'accusttranses_match_auto_show_title', translateTitle: true, message: errormessage });
            } else {
                this.readGrid();
            }
        } catch (error) {
            console.error(error);
        } finally {
            this.autoMatching = false;
        }
    }

    private loadGrid(): void {
        if (!this.page) return;

        if (!this.grid) {
            this.grid = new Grid<AcCustTransesGridRow>()
                .setRememberId('w_accusttranses_grid')
                .setLoadDataFunction({ method: 2795, parameters: () => this.getGridParams() })
                .setSpecialFunc({ newTab: true })
                .setOptionFunc((event: AcCustTransesGridEvent) => this.optionFunc(event.data));
        } else {
            this.readGridWithColschema();
        }
    }

    private getGridParams(): Omit<AcCustTransesGet, 'customers'> & { acc_cust_nos: string } {
        const { customers, ...page } = this.page!;

        return { ...page, acc_cust_nos: customers.map(({ acc_cust_no }) => acc_cust_no).join(',') };
    }

    private optionFunc(event: AcCustTransesGridOption): void {
        if (event.func === 'CheckboxBoxClick' && event.dataItem) {
            event.dataItem.is_selected_amount_locval_balance = event.dataItem.is_selected === true ? event.dataItem.amount_locval_balance : 0;
        } else if (event.func === 'CheckboxHeaderClick') {
            event.data?.forEach((row) => {
                row.is_selected_amount_locval_balance = row.is_selected === true ? row.amount_locval_balance : 0;
            });
        } else {
            return;
        }

        this.grid?.gridfunc?.refresh();
        this.grid?.gridfunc?.refreshAggregates();
    }

    private async init(): Promise<void> {
        if (!this.params) return;

        try {
            const [page, companies] = await Promise.all([this.acCustTrans.getAcCustTranses(this.params), this.logistic.getCompanies()]);

            this.page = page;
            this.companies = companies;
            this.navigator?.setHeading?.(page.page_heading);
            this.loadGrid();
        } catch (error) {
            console.error(error);
        }
    }

    ngOnInit(): void {
        if (this.navigator) {
            this.navigator.setSize?.({ width: '95vw', height: '98vh' });
            this.params = this.navigator.params as unknown as GetAcCustTransesParams;
        }

        this.init();
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['params'] && !changes['params'].firstChange) {
            this.init();
        }
    }
}

interface AcCustTransesGridEvent {
    data: AcCustTransesGridOption;
}

interface AcCustTransesGridOption {
    func?: 'CheckboxBoxClick' | 'CheckboxHeaderClick' | string;
    dataItem?: AcCustTransesGridRow;
    data?: AcCustTransesGridRow[];
}
